<template>
  <div v-if="position" class="map-context-menu" :style="{ top: `${position.y}px`, left: `${position.x}px` }">
    <div class="map-context-menu__header" :class="{ 'map-context-menu__header--single': options.length === 1 }">
      <v-autocomplete
        v-if="options.length > 1"
        v-model="selectedFid"
        class="map-context-menu__header__select"
        :items="options"
        item-text="label"
        item-value="fid"
        hide-details
        dense
        outlined
        :menu-props="{ contentClass: 'context-menu-dropdown-content' }"
      />
      <div v-else class="map-context-menu__header__title">
        {{ displayText }}
      </div>
    </div>
    <div class="map-context-menu__subtitle">
      {{ $t("multiple-selection.easy-add-edit-popup.feature-actions-title") }}
    </div>
    <div class="map-context-menu__actions">
      <v-btn
        id="map-context-menu__btn--edit"
        type="button"
        class="map-context-menu__btn"
        plain
        depressed
        small
        :disabled="options.length === 0"
        @click="onEdit"
      >
        <PtrIcon
          class="mr-3"
          :icon="isReadOnly ? 'info-square' : 'modify-geometry'"
          width="12"
          height="12"
          :class="{ 'info-icon': isReadOnly }"
        />
        <div>
          {{
            isReadOnly
              ? $t("multiple-selection.easy-add-edit-popup.view")
              : $t("multiple-selection.easy-add-edit-popup.edit")
          }}
        </div>
      </v-btn>
      <v-btn
        v-if="!isReadOnly"
        id="map-context-menu__btn--copy"
        type="button"
        class="map-context-menu__btn"
        plain
        depressed
        small
        :disabled="options.length === 0"
        @click="onCopy"
      >
        <PtrIcon class="mr-3" icon="copy-07-gray" width="12" height="12" />
        <div>{{ $t("multiple-selection.easy-add-edit-popup.copy") }}</div>
      </v-btn>
      <v-btn
        v-if="!isReadOnly"
        id="map-context-menu__btn--clone"
        type="button"
        class="map-context-menu__btn"
        plain
        depressed
        small
        :disabled="options.length === 0"
        @click="onClone"
      >
        <PtrIcon
          class="mr-3"
          icon="plus-square"
          width="12"
          height="12"
          editable-color
          stroke-color="foreground-lighten1"
          color="background-lighten5"
        />
        <div>{{ $t("multiple-selection.easy-add-edit-popup.clone") }}</div>
      </v-btn>
    </div>
    <div v-if="!isReadOnly" class="map-context-menu__subtitle">
      {{ $t("multiple-selection.easy-add-edit-popup.clipboard") }}
    </div>
    <div v-if="!isReadOnly" class="map-context-menu__actions">
      <v-btn
        id="map-context-menu__btn--paste-and-edit"
        type="button"
        class="map-context-menu__btn"
        plain
        depressed
        small
        :disabled="copiedMapContentFeatures.length === 0"
        @click="onPasteAndEdit"
      >
        <PtrIcon class="mr-3" icon="paste" width="12" height="12" />
        <div>{{ $t("multiple-selection.easy-add-edit-popup.paste-here") }}</div>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import PtrIcon from "@/components/shared/PtrIcon.vue";
export default {
  name: "EasyAddEditPopup",
  components: {
    PtrIcon
  },
  props: {
    position: {
      type: Object,
      required: false
    },
    features: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data: () => ({
    selectedFid: null
  }),
  computed: {
    ...mapState("CONTENT", ["mapObjects", "selectedFeatures", "copiedMapContentFeatures"]),
    ...mapState("MAP", ["currentClient", "currentSite"]),
    options() {
      const options = (this.features || [])
        .map((feature) => {
          const { fid, name, typeCode } = feature.properties || {};
          if (fid === undefined) return null;
          const isNameValid = name && name.trim() !== "";
          return {
            fid,
            label: isNameValid ? name : `${typeCode}: ${fid}`,
            feature
          };
        })
        .filter(Boolean);
      return options;
    },
    selectedFeature() {
      if (!this.options.length) {
        return null;
      }
      const fid = this.selectedFid || this.options[0].fid;
      const option = this.options.find((opt) => opt.fid === fid);
      return option ? option.feature : null;
    },
    displayText() {
      if (this.features?.length === 1) {
        console.log(this.features[0]);
        return (
          this.features[0].properties.name ||
          `${this.features[0].properties.typeCode}: ${this.features[0].properties.fid}`
        );
      } else if (this.copiedMapContentFeatures?.length > 0) {
        return this.$t("multiple-selection.items-copied", { count: this.copiedMapContentFeatures.length });
      } else if (this.selectedFeatures?.length > 0) {
        return this.$t("multiple-selection.items-selected", { count: this.selectedFeatures.length });
      }
      return "";
    },
    isFeatureSelectedOnMap() {
      return this.selectedFeatures.some((feature) => feature.properties.fid === this.selectedFeature?.properties?.fid);
    },
    isReadOnly() {
      const permissions = this.$store.state.permissionObject;
      return !(
        permissions?.clients?.[this.currentClient] === 1 ||
        permissions?.clients?.[this.currentClient] === 2 ||
        permissions?.sites?.[this.currentSite] === 1 ||
        permissions?.sites?.[this.currentSite] === 2
      );
    }
  },
  watch: {
    features: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length) {
          const firstFid = newVal[0]?.properties?.fid;
          this.selectedFid = firstFid ?? null;
          // Ensure initial selection is also highlighted (covers single-option case)
          this.$emit("feature-selected", {
            feature: newVal[0],
            position: this.position
          });
        } else {
          this.selectedFid = null;
        }
      }
    },
    selectedFid() {
      if (!this.selectedFeature) return;
      this.$emit("feature-selected", {
        feature: this.selectedFeature,
        position: this.position
      });
    }
  },
  methods: {
    onCopy() {
      if (this.selectedFeatures.some((feature) => feature.properties.fid === this.selectedFeature?.properties?.fid)) {
        this.$emit("copy-selected-features");
        return;
      }
      if (!this.selectedFeature) return;
      const mapObject = this.mapObjects?.find(
        (mapObject) => mapObject.properties.fid === this.selectedFeature.properties.fid
      );
      if (!mapObject) return;
      this.$emit("copy", {
        feature: mapObject,
        position: this.position
      });
    },
    onPasteAndEdit() {
      let featuresToPaste =
        this.copiedMapContentFeatures.length > 0 ? this.copiedMapContentFeatures : [this.selectedFeature];
      if (featuresToPaste.length === 0) {
        return;
      }
      this.$emit("paste-and-edit", {
        features: featuresToPaste,
        position: this.position
      });
    },
    onClone() {
      if (!this.selectedFeatures && !this.selectedFeature) {
        return;
      }
      if (this.selectedFeature && this.selectedFeatures.length <= 1) {
        const mapObject = this.mapObjects?.find(
          (mapObject) => mapObject.properties.fid === this.selectedFeature.properties.fid
        );
        this.$emit("clone", {
          feature: mapObject,
          position: this.position
        });
      } else if (this.selectedFeatures.length > 0) {
        this.$emit("multiple-clone", {
          features: this.selectedFeatures,
          position: this.position
        });
      }
    },
    onEdit() {
      if (!this.selectedFeatures && !this.selectedFeature) {
        return;
      }
      if (this.selectedFeature && this.selectedFeatures.length <= 1) {
        this.$emit("edit", {
          feature: this.selectedFeature,
          position: this.position
        });
      } else if (this.selectedFeatures.length > 0) {
        this.$emit("multiple-edit", {
          features: this.selectedFeatures
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.map-context-menu {
  position: absolute;
  background-color: #fff;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px #0000001a, 0 8px 10px -6px #0000001a;
  min-width: 200px;
  pointer-events: auto;
  z-index: 20;
}

.map-context-menu__actions {
  display: flex;
  flex-direction: column;
  padding: 0px 0px 8px;
}

.map-context-menu__btn {
  min-width: 120px;
}

.map-context-menu__header {
  color: #6b7280;
  color: var(--text-secondary, #6b7280);
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 4px;
  max-width: 220px;
  text-overflow: ellipsis;
  padding: 8px 16px 8px;

  &.map-context-menu__header--single {
    border-bottom: 1px solid var(--border-color, #f3f4f6);
  }
}

.map-context-menu__header__select {
  cursor: pointer;
}

.map-context-menu__subtitle {
  color: #94a3b8;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 4px 16px;
}

.v-btn[disabled]:hover {
  border-color: transparent !important;
}

#map-context-menu__btn--copy,
#map-context-menu__btn--clone,
#map-context-menu__btn--edit,
#map-context-menu__btn--paste,
#map-context-menu__btn--paste-and-edit {
  box-shadow: none !important;

  ::v-deep .v-btn__content {
    padding: 8px 16px;
    justify-content: flex-start !important;
    color: var(--text-primary, #374151);
    font-size: 13px;
    font-weight: 400;
  }
}

::v-deep .v-autocomplete.v-input > .v-input__control > .v-input__slot {
  cursor: pointer !important;

  * {
    cursor: pointer !important;
  }
}

.info-icon {
  margin-top: 2px !important;
}
</style>
